import { TimezoneUtils } from "../utils/timezoneUtils";
import { apiCall, FUNCTIONS_BASE_URL } from "./firebase";

// Provider-agnostic calendar types
export interface CalendarProvider {
  id: string;
  name: string;
  type: string;
  isConnected: boolean;
  config?: any;
}

export interface CalendarEvent {
  id: string;
  userId: string;
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  startTimeUTC: Date;
  endTimeUTC: Date;
  allDay: boolean;
  location?: string;
  meetingLink?: string; // Meeting link (e.g., Zoom, Teams, Google Meet)
  timezone?: string; // Event timezone, defaults to "UTC" if not provided
  attendees?: Array<{
    email: string;
    displayName: string;
    responseStatus: "accepted" | "declined" | "tentative" | "needsAction";
  }>;
  status: "confirmed" | "tentative" | "cancelled";
  color?: string;
  classification?: string;
  providerId: string;
  providerEventId?: string;
  syncStatus: "synced" | "pending_sync" | "sync_failed" | "conflict";
  lastSyncedAt?: Date;
  lastModifiedAt: Date;
  createdAt: Date;
  updatedAt: Date;
  // Recurring event fields
  recurrence?: {
    frequency: "daily" | "weekly" | "monthly" | "yearly";
    interval?: number;
    endDate?: Date;
    endCount?: number;
    daysOfWeek?: number[];
    position?: number;
  };
  isBaseEvent?: boolean; // True for base recurring events
  baseEventId?: string; // For instances, reference to base event
  rrule?: string; // Google Calendar RRULE format
  originalStartTime?: Date; // For exceptions, in UTC
  sequence?: number; // Sequence number for recurring events (1 for base event)
  isException?: boolean; // True for modified instances
  visibility: "public" | "private" | "default";
  // Invitation response fields
  userEmail?: string; // User's email address (stored for efficiency)
  isInvited?: boolean; // True if current user is an attendee
  userResponseStatus?: "needsAction" | "declined" | "tentative" | "accepted"; // Current user's response
  organizerEmail?: string; // Email of the event organizer
  // Event permissions
  editable?: boolean; // True if the current user can edit this event
  external?: boolean; // True if this event is organized by someone else
}

export interface CalendarSyncStatus {
  userId: string;
  providerType: string;
  lastSyncFromProvider: Date;
  lastSyncToProvider: Date;
  status: "syncing" | "success" | "error" | "conflict";
  error?: string;
  eventsCount: number;
  pendingSyncCount: number;
  conflictCount: number;
  lastEventId?: string;
  syncMode: "immediate" | "batch" | "manual";
  nextScheduledSync?: Date;
}

export interface CreateEventRequest {
  title: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  allDay?: boolean;
  location?: string;
  timezone?: string; // Event timezone, defaults to "UTC" if not provided
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
  status?: "confirmed" | "tentative" | "cancelled";
  color?: string;
  classification?: string;
}

export interface UpdateEventRequest {
  title?: string;
  description?: string;
  startTime?: Date;
  endTime?: Date;
  allDay?: boolean;
  location?: string;
  timezone?: string; // Event timezone, defaults to "UTC" if not provided
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
  status?: "confirmed" | "tentative" | "cancelled";
  color?: string;
  classification?: string;
}

export interface CalendarQuery {
  timeRange?: {
    start: Date;
    end: Date;
  };
  search?: string;
  status?: string[];
  limit?: number;
  offset?: number;
}

class CalendarService {
  // Get current calendar provider
  async getCurrentProvider(): Promise<CalendarProvider | null> {
    try {
      console.log("CalendarService: Getting current provider...");
      const response = await apiCall("/getCurrentProvider");

      if (!response.success) {
        console.log("CalendarService: No provider connected");
        return null;
      }

      return response.provider;
    } catch (error) {
      console.error("CalendarService: Failed to get current provider:", error);
      return null;
    }
  }

  // Get available calendar providers
  async getAvailableProviders(): Promise<CalendarProvider[]> {
    try {
      console.log("CalendarService: Getting available providers...");
      const response = await apiCall("/getAvailableProviders");

      if (!response.success) {
        throw new Error(response.error || "Failed to get available providers");
      }

      return response.providers || [];
    } catch (error) {
      console.error("CalendarService: Failed to get available providers:", error);
      throw error;
    }
  }

  // Connect to a calendar provider
  async connectProvider(providerType: string, config: any): Promise<boolean> {
    try {
      console.log(`CalendarService: Connecting to provider ${providerType}...`);
      const response = await apiCall("/connectCalendarProvider", {
        method: "POST",
        body: JSON.stringify({
          providerType,
          config,
        }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to connect provider");
      }

      console.log(`CalendarService: Successfully connected to ${providerType}`);
      return true;
    } catch (error) {
      console.error("CalendarService: Failed to connect provider:", error);
      throw error;
    }
  }

  // Get calendar events
  async getEvents(query: CalendarQuery = {}): Promise<CalendarEvent[]> {
    try {
      console.log("CalendarService: Fetching calendar events...");

      const params = new URLSearchParams();

      if (query.timeRange) {
        params.append(
          "timeRange",
          JSON.stringify({
            start: TimezoneUtils.convertToString(query.timeRange.start),
            end: TimezoneUtils.convertToString(query.timeRange.end),
          })
        );
      }

      if (query.search) {
        params.append("search", query.search);
      }

      if (query.status && query.status.length > 0) {
        params.append("status", query.status.join(","));
      }

      if (query.limit) {
        params.append("limit", query.limit.toString());
      }

      if (query.offset) {
        params.append("offset", query.offset.toString());
      }

      const response = await apiCall(`/getCalendarEvents?${params.toString()}`);

      if (!response.success) {
        throw new Error(response.error || "Failed to fetch calendar events");
      }

      // Convert date strings back to Date objects
      const events: CalendarEvent[] = (response.events || []).map((event: any) => ({
        ...event,
        startTime: new Date(event.startTime.replace("Z", "")),
        endTime: new Date(event.endTime.replace("Z", "")),
        startTimeUTC: new Date(event.startTimeUTC.replace("Z", "")),
        endTimeUTC: new Date(event.endTimeUTC.replace("Z", "")),
        lastSyncedAt: event.lastSyncedAt ? new Date(event.lastSyncedAt) : undefined,
        lastModifiedAt: new Date(event.lastModifiedAt),
        createdAt: new Date(event.createdAt),
        updatedAt: new Date(event.updatedAt),
      }));

      console.log(`CalendarService: Fetched ${events.length} events`);
      return events;
    } catch (error) {
      console.error("CalendarService: Failed to fetch events:", error);
      throw error;
    }
  }

  // Create a new event
  async createEvent(eventData: CreateEventRequest): Promise<CalendarEvent> {
    console.log("CalendarService: Creating event...", eventData);
    console.log("Data: ", eventData);
    const formattedEventData = {
      ...eventData,
      startTime: eventData.startTime ? TimezoneUtils.convertToString(eventData.startTime) : undefined,
      endTime: eventData.endTime ? TimezoneUtils.convertToString(eventData.endTime) : undefined,
    };
    console.log("Formatted eventData: ", formattedEventData);
    try {
      console.log("CalendarService: Creating event...");
      const response = await apiCall("/upsertCalendarEvent", {
        method: "POST",
        body: JSON.stringify(formattedEventData),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to create event");
      }

      console.log("CalendarService: Event created successfully");
      return response.event;
    } catch (error) {
      console.error("CalendarService: Error creating event:", error);
      throw error;
    }
  }

  // Update an existing event
  async updateEvent(eventId: string, updates: UpdateEventRequest): Promise<CalendarEvent> {
    try {
      console.log(`CalendarService: Updating event ${eventId}...`);
      console.log("Updates: ", updates);
      const formattedUpdates = {
        ...updates,
        startTime: updates.startTime ? TimezoneUtils.convertToString(updates.startTime) : undefined,
        endTime: updates.endTime ? TimezoneUtils.convertToString(updates.endTime) : undefined,
      };
      console.log("Formatted updates: ", formattedUpdates);
      const response = await apiCall("/upsertCalendarEvent", {
        method: "POST",
        body: JSON.stringify({ eventId, ...formattedUpdates }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to update event");
      }

      console.log("CalendarService: Event updated successfully");
      return response.event;
    } catch (error) {
      console.error("CalendarService: Error updating event:", error);
      throw error;
    }
  }

  // Delete an event
  async deleteEvent(eventId: string): Promise<void> {
    try {
      console.log(`CalendarService: Deleting event ${eventId}...`);

      const response = await apiCall("/deleteCalendarEvent", {
        method: "DELETE",
        body: JSON.stringify({ eventId }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to delete event");
      }

      console.log("CalendarService: Event deleted successfully");
    } catch (error) {
      console.error("CalendarService: Failed to delete event:", error);
      throw error;
    }
  }

  // Move event to new time
  async moveEvent(eventId: string, newStartTime: Date, newEndTime: Date, notifyAttendees?: boolean): Promise<CalendarEvent> {
    try {
      console.log(`CalendarService: Moving event ${eventId}...`);
      const response = await apiCall("/moveCalendarEvent", {
        method: "POST",
        body: JSON.stringify({
          eventId,
          newStartTime: TimezoneUtils.convertToString(newStartTime),
          newEndTime: TimezoneUtils.convertToString(newEndTime),
          notifyAttendees,
        }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to move event");
      }

      console.log(`CalendarService: Successfully moved event ${eventId}`);
      return response.event;
    } catch (error) {
      console.error(`CalendarService: Error moving event ${eventId}:`, error);
      throw error;
    }
  }

  // Manage event attendees
  async manageAttendees(eventId: string, action: "add" | "remove" | "update", attendees: string[], sendInvites?: boolean): Promise<CalendarEvent> {
    try {
      console.log(`CalendarService: Managing attendees for event ${eventId}, action: ${action}...`);
      const response = await apiCall("/manageEventAttendees", {
        method: "POST",
        body: JSON.stringify({
          eventId,
          action,
          attendees,
          sendInvites,
        }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to manage attendees");
      }

      console.log(`CalendarService: Successfully managed attendees for event ${eventId}`);
      return response.event;
    } catch (error) {
      console.error(`CalendarService: Error managing attendees for event ${eventId}:`, error);
      throw error;
    }
  }

  // Get sync status
  async getSyncStatus(): Promise<CalendarSyncStatus | null> {
    try {
      console.log("CalendarService: Getting sync status...");
      const response = await apiCall("/getSyncStatus");

      if (!response.success || !response.syncStatus) {
        console.log("CalendarService: No sync status available");
        return null;
      }

      // Convert date strings back to Date objects
      const syncStatus: CalendarSyncStatus = {
        ...response.syncStatus,
        lastSyncFromProvider: response.syncStatus.lastSyncFromProvider ? new Date(response.syncStatus.lastSyncFromProvider) : new Date(),
        lastSyncToProvider: response.syncStatus.lastSyncToProvider ? new Date(response.syncStatus.lastSyncToProvider) : new Date(),
        nextScheduledSync: response.syncStatus.nextScheduledSync ? new Date(response.syncStatus.nextScheduledSync) : undefined,
      };

      return syncStatus;
    } catch (error) {
      console.error("CalendarService: Failed to get sync status:", error);
      return null;
    }
  }

  // Trigger manual sync
  async triggerManualSync(): Promise<{ success: boolean; eventsCount: number; error?: string }> {
    try {
      console.log("CalendarService: Triggering manual sync...");
      const response = await apiCall("/triggerManualSync", {
        method: "POST",
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to trigger manual sync");
      }

      console.log(`CalendarService: Manual sync completed with ${response.eventsCount} events`);
      return {
        success: response.success,
        eventsCount: response.eventsCount,
        error: response.error,
      };
    } catch (error) {
      console.error("CalendarService: Failed to trigger manual sync:", error);
      throw error;
    }
  }

  // Trigger calendar sync from provider
  async syncFromProvider(): Promise<{ success: boolean; eventsCount: number; error?: string }> {
    try {
      console.log("CalendarService: Triggering sync from provider...");
      const response = await apiCall("/syncCalendarProvider", {
        method: "POST",
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to sync from provider");
      }

      console.log(`CalendarService: Sync from provider completed with ${response.eventsCount} events`);
      return {
        success: response.success,
        eventsCount: response.eventsCount,
        error: response.error,
      };
    } catch (error) {
      console.error("CalendarService: Failed to sync from provider:", error);
      throw error;
    }
  }

  // Sync and get events in one call
  async syncAndGetEvents(query: CalendarQuery = {}): Promise<CalendarEvent[]> {
    try {
      console.log("CalendarService: Syncing and getting events...");

      // First trigger the sync
      await this.syncFromProvider();

      // Then get the events
      return await this.getEvents(query);
    } catch (error) {
      console.error("CalendarService: Failed to sync and get events:", error);
      throw error;
    }
  }

  // Schedule periodic sync
  async schedulePeriodicSync(intervalMinutes = 15): Promise<void> {
    try {
      console.log(`CalendarService: Scheduling periodic sync every ${intervalMinutes} minutes...`);
      const response = await apiCall("/schedulePeriodicSync", {
        method: "POST",
        body: JSON.stringify({ intervalMinutes }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to schedule periodic sync");
      }

      console.log("CalendarService: Periodic sync scheduled successfully");
    } catch (error) {
      console.error("CalendarService: Failed to schedule periodic sync:", error);
      throw error;
    }
  }

  // Get weekly events (helper method for calendar view)
  async getWeeklyEvents(startDate: Date): Promise<CalendarEvent[]> {
    const startOfWeek = new Date(startDate);
    startOfWeek.setDate(startDate.getDate() - startDate.getDay() + 1); // Monday
    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 7); // Next Monday
    endOfWeek.setHours(23, 59, 59, 999);

    return this.getEvents({
      timeRange: {
        start: startOfWeek,
        end: endOfWeek,
      },
      limit: 100,
    });
  }

  // Get upcoming events (helper method)
  async getUpcomingEvents(maxResults = 10): Promise<CalendarEvent[]> {
    const now = new Date();
    const future = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000); // 30 days from now

    return this.getEvents({
      timeRange: {
        start: now,
        end: future,
      },
      limit: maxResults,
    });
  }

  // Check if user has a connected provider
  async hasConnectedProvider(): Promise<boolean> {
    const provider = await this.getCurrentProvider();
    return provider !== null && provider.isConnected;
  }

  // Update a single instance of a recurring event
  async updateEventInstance(eventId: string, updates: UpdateEventRequest): Promise<CalendarEvent> {
    try {
      console.log(`CalendarService: Updating recurring event instance ${eventId}...`);
      console.log("Updates: ", updates);
      const formattedUpdates = {
        ...updates,
        startTime: updates.startTime ? TimezoneUtils.convertToString(updates.startTime) : undefined,
        endTime: updates.endTime ? TimezoneUtils.convertToString(updates.endTime) : undefined,
      };
      console.log("Formatted updates: ", formattedUpdates);
      const response = await apiCall("/updateEventInstance", {
        method: "POST",
        body: JSON.stringify({ eventId, ...formattedUpdates }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to update event instance");
      }

      console.log("CalendarService: Event instance updated successfully");
      return response.event;
    } catch (error) {
      console.error("CalendarService: Error updating event instance:", error);
      throw error;
    }
  }

  // Update the entire series of a recurring event
  async updateEventSeries(baseEventId: string, updates: UpdateEventRequest): Promise<CalendarEvent> {
    try {
      console.log(`CalendarService: Updating recurring event series ${baseEventId}...`);
      console.log("Updates: ", updates);
      const formattedUpdates = {
        ...updates,
        startTime: updates.startTime ? TimezoneUtils.convertToString(updates.startTime) : undefined,
        endTime: updates.endTime ? TimezoneUtils.convertToString(updates.endTime) : undefined,
      };
      console.log("Formatted updates: ", formattedUpdates);
      const response = await apiCall("/updateEventSeries", {
        method: "POST",
        body: JSON.stringify({ baseEventId, ...formattedUpdates }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to update event series");
      }

      console.log("CalendarService: Event series updated successfully");
      return response.event;
    } catch (error) {
      console.error("CalendarService: Error updating event series:", error);
      throw error;
    }
  }

  // Delete a single instance of a recurring event
  async deleteEventInstance(eventId: string): Promise<void> {
    try {
      console.log(`CalendarService: Deleting recurring event instance ${eventId}...`);
      const response = await apiCall("/deleteEventInstance", {
        method: "DELETE",
        body: JSON.stringify({ eventId }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to delete event instance");
      }

      console.log("CalendarService: Event instance deleted successfully");
    } catch (error) {
      console.error("CalendarService: Failed to delete event instance:", error);
      throw error;
    }
  }

  // Delete the entire series of a recurring event
  async deleteEventSeries(baseEventId: string): Promise<void> {
    try {
      console.log(`CalendarService: Deleting recurring event series ${baseEventId}...`);
      const response = await apiCall("/deleteEventSeries", {
        method: "DELETE",
        body: JSON.stringify({ baseEventId }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to delete event series");
      }

      console.log("CalendarService: Event series deleted successfully");
    } catch (error) {
      console.error("CalendarService: Failed to delete event series:", error);
      throw error;
    }
  }

  // Update this and future instances of a recurring event
  async updateEventFutureInstances(eventId: string, updates: UpdateEventRequest): Promise<{ originalBaseEvent: CalendarEvent; newBaseEvent: CalendarEvent }> {
    try {
      console.log(`CalendarService: Updating recurring event this and future ${eventId}...`);
      console.log("Updates: ", updates);
      const formattedUpdates = {
        ...updates,
        startTime: updates.startTime ? TimezoneUtils.convertToString(updates.startTime) : undefined,
        endTime: updates.endTime ? TimezoneUtils.convertToString(updates.endTime) : undefined,
      };
      console.log("Formatted updates: ", formattedUpdates);
      const response = await apiCall("/updateEventFutureInstances", {
        method: "POST",
        body: JSON.stringify({ eventId, ...formattedUpdates }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to update event future instances");
      }

      console.log("CalendarService: Event future instances updated successfully");
      return response;
    } catch (error) {
      console.error("CalendarService: Error updating event future instances:", error);
      throw error;
    }
  }

  // Delete this and future instances of a recurring event
  async deleteEventFutureInstances(eventId: string): Promise<CalendarEvent> {
    try {
      console.log(`CalendarService: Deleting recurring event future instances ${eventId}...`);
      const response = await apiCall("/deleteEventFutureInstances", {
        method: "DELETE",
        body: JSON.stringify({ eventId }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to delete event future instances");
      }

      console.log("CalendarService: Event future instances deleted successfully");
      return response.updatedBaseEvent;
    } catch (error) {
      console.error("CalendarService: Failed to delete event future instances:", error);
      throw error;
    }
  }

  // Respond to a calendar event invitation
  async respondToEvent(eventId: string, responseStatus: "accepted" | "declined" | "tentative"): Promise<CalendarEvent> {
    try {
      console.log(`CalendarService: Responding to event ${eventId} with status ${responseStatus}...`);
      const response = await apiCall("/respondToEvent", {
        method: "POST",
        body: JSON.stringify({ eventId, response: responseStatus }),
      });

      if (!response.success) {
        throw new Error(response.error || "Failed to respond to event");
      }

      console.log("CalendarService: Event response updated successfully");
      return response.event;
    } catch (error) {
      console.error("CalendarService: Failed to respond to event:", error);
      throw error;
    }
  }
}

// Create singleton instance
export const calendarService = new CalendarService();
